"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Timber = void 0;
var enums_1 = require("../enums");
var config_default_1 = require("../config.default");
var appenders_population_1 = require("../tools/appenders.population");
var config_default_2 = require("../config.default");
/**
 * Represents an instance of the client-side logger.
 */
var Timber = /** @class */ (function () {
    /**
     * @constructor
     * @param config initial config you would like the application to represent.
     * Can be empty - in that case {@link defaultConfig} will be used.
     */
    function Timber(config) {
        this.config = __assign({}, config_default_1.defaultConfig);
        if (config) {
            this.setConfig(config);
        }
    }
    /**
    * Log the message with {@link LogLevel.Trace} log level.
    * Designates finer-grained informational events than the {@link LogLevel.Debug}. usually used in case of investigation and enables on demand, since it contain more details and may significantly bloat the log size.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.trace = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Trace, message, parameters);
    };
    /**
    * Log the message with {@link LogLevel.Debug} log level.
    * Designates fine-grained informational events that are most useful to debug an application.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.debug = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Debug, message, parameters);
    };
    /**
    * Log the message with {@link LogLevel.Info} log level.
    * Designates informational messages that highlight the progress of the application at coarse-grained level.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.info = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Info, message, parameters);
    };
    /**
    * Log the message with {@link LogLevel.Warn} log level.
    * Designates potentially harmful situations.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.warn = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Warn, message, parameters);
    };
    /**
    * Log the message with {@link LogLevel.Error} log level.
    * Designates error events that might still allow the application to continue running.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.error = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Error, message, parameters);
    };
    /**
    * Log the message with {@link LogLevel.Fatal} log level.
    * Designates very severe error events that will presumably lead the application to abort.
    * @param {string} message - message to log
    * @param {ILogMessage} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.fatal = function (message, parameters) {
        if (parameters === void 0) { parameters = {}; }
        this.log(enums_1.LogLevel.Fatal, message, parameters);
    };
    /**
     * Once you already instantiated {@link Timber} instance, you may override its {@link IConfig} config using this method.
     * @param {IConfig} config - config to be overridden. May contain only partial data that you want to override.
     * @param {ISetConfigOptions | undefined} setConfigOptions - auxiliary config defining the logger mode, ability to explicitly set the appenders, etc.
     */
    Timber.prototype.setConfig = function (config, setConfigOptions) {
        config = __assign(__assign({}, this.config), config);
        if (!setConfigOptions) {
            setConfigOptions = __assign({}, config_default_2.defaultSetConfigOptions);
        }
        this.config = (0, appenders_population_1.populateAppenders)(this.config, config, setConfigOptions);
    };
    /**
    * General purpose log function. Can be used as alternative to specific log
    * functions sutch as trace or debug
    * @param {LogLevel} logLevel - log level according to importance of the message
    * @param {string | Object} message - message to log, can accept object that will be serialized
    * @param {ILogParameters} parameters - additional info, as module name, traceId or error.
    */
    Timber.prototype.log = function (logLevel, message, parameters) {
        var _this = this;
        if (this.config.minLogLevel > logLevel) {
            return;
        }
        if (!this.config.appenders || this.config.appenders.length === 0) {
            throw new Error('Please specify at least one appender for logger');
        }
        this.config.appenders.map(function (a) {
            var logMessage = _this.getLogMessage(logLevel, _this.config, message, parameters);
            a.action(logMessage, _this.config);
        });
    };
    /**
    * General purpose biLog function. This function is used only to do BI Log
    * @param {LogLevel} logLevel - log level according to importance of the message
    * @param {string | Object} message - message to log, can accept object that will be serialized
    * @param {ILogParameters} parameters - additional info, as module name, traceId or error.
    * @param {Object} logHeaders - Required Headers need to send for BI Logs.
    */
    Timber.prototype.biLog = function (logLevel, message, parameters, logHeaders) {
        var _this = this;
        var _a;
        if (logHeaders === void 0) { logHeaders = {}; }
        if (this.config.minLogLevel > logLevel) {
            return;
        }
        if (!((_a = this.config.appenders) === null || _a === void 0 ? void 0 : _a.length)) {
            throw new Error('Please specify at least one appender for logger');
        }
        if (logHeaders == undefined || Object.values(logHeaders).length == 0) {
            logHeaders = { eventlogtype: 'BI' };
        }
        this.config.appenders.map(function (a) {
            var logMessage = __assign(__assign({}, _this.getBILogMessage(logLevel, parameters)), { logHeaders: logHeaders });
            a.action(logMessage, _this.config);
        });
    };
    Timber.prototype.getBILogMessage = function (logLevel, parameters) {
        return {
            date: this.getCurrentDate(),
            level: enums_1.LogLevel[logLevel],
            value: { value: '' },
            url: window.location.href,
            extendedParameters: parameters.extendedParameters
        };
    };
    Timber.prototype.getLogMessage = function (logLevel, config, message, parameters) {
        return {
            date: this.getCurrentDate(),
            level: enums_1.LogLevel[logLevel],
            value: { value: message },
            exception: this.createException(parameters.error),
            url: window.location.href,
            module: parameters.module || config.module,
            appId: config.appId,
            companyId: config.companyId,
            userId: config.userId,
            traceId: parameters.traceId,
            eventType: parameters.eventType || config.eventType,
            extendedParameters: parameters.extendedParameters
        };
    };
    Timber.prototype.getCurrentDate = function () {
        var date = new Date();
        return {
            localDate: date.toString(),
            utcDate: date.toUTCString(),
        };
    };
    Timber.prototype.createException = function (exception) {
        if (typeof (exception === null || exception === void 0 ? void 0 : exception.error) === 'string' && exception.error.includes('DOCTYPE html')) {
            exception.error = {};
        }
        return exception;
    };
    return Timber;
}());
exports.Timber = Timber;
//# sourceMappingURL=Timber.js.map